import type { DocumentPickerAsset } from "expo-document-picker";
import * as ImagePicker from "expo-image-picker";

import { readUploadBody } from "./submissions";
import { supabase } from "./supabase";

export type AvatarUploadFile = {
  uri: string;
  name: string;
  mimeType?: string;
  size?: number;
  file?: File;
};

export async function pickAvatarFile(): Promise<AvatarUploadFile | null> {
  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ImagePicker.MediaTypeOptions.Images,
    allowsEditing: true,
    aspect: [1, 1],
    quality: 0.85,
  });
  if (result.canceled || !result.assets?.[0]) return null;
  const asset = result.assets[0];
  const filename = asset.fileName ?? `avatar-${Date.now()}.jpg`;
  const size =
    typeof asset.fileSize === "number"
      ? asset.fileSize
      : (asset as { file?: { size?: number } }).file?.size;
  return {
    uri: asset.uri,
    name: filename,
    mimeType: asset.mimeType ?? "image/jpeg",
    size,
    file: (asset as { file?: File }).file,
  };
}

export async function removeAvatar(): Promise<void> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) throw new Error("Not signed in");
  const userId = userData.user.id;

  const { data: profile } = await supabase
    .from("user_profiles")
    .select("avatar_url")
    .eq("id", userId)
    .maybeSingle();

  const currentUrl = typeof profile?.avatar_url === "string" ? profile.avatar_url : null;

  // Best-effort delete from Storage. Derive ext from the public URL when present
  // so we hit the right object; otherwise fall back to common extensions. The
  // updated() metadata write is what users actually depend on, so do not throw
  // if Storage delete misses.
  const candidateExts = new Set<string>(["jpg", "jpeg", "png", "webp", "gif"]);
  if (currentUrl) {
    const match = currentUrl.match(/\/avatar\.([a-z0-9]+)(?:\?|$)/i);
    if (match) candidateExts.add(match[1].toLowerCase());
  }
  const paths = Array.from(candidateExts).map((ext) => `${userId}/avatar.${ext}`);
  await supabase.storage.from("pictures").remove(paths);

  const { error: updateError } = await supabase
    .from("user_profiles")
    .update({ avatar_url: null })
    .eq("id", userId);
  if (updateError) throw new Error(updateError.message);
}

export async function uploadAvatar(file: AvatarUploadFile): Promise<string> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) throw new Error("Not signed in");
  const userId = userData.user.id;

  const ext = file.name.includes(".") ? file.name.split(".").pop()?.toLowerCase() ?? "jpg" : "jpg";
  const path = `${userId}/avatar.${ext}`;
  const { body, contentType: detectedType } = await readUploadBody(file as DocumentPickerAsset);

  const { error } = await supabase.storage.from("pictures").upload(path, body, {
    upsert: true,
    contentType: file.mimeType ?? detectedType ?? "image/jpeg",
  });
  if (error) throw new Error(error.message);

  const { data: publicData } = supabase.storage.from("pictures").getPublicUrl(path);
  const publicUrl = publicData.publicUrl;

  const { error: updateError } = await supabase.from("user_profiles").update({ avatar_url: publicUrl }).eq("id", userId);
  if (updateError) throw new Error(updateError.message);

  return publicUrl;
}

export async function uploadResume(file: DocumentPickerAsset): Promise<string> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) throw new Error("Not signed in");
  const userId = userData.user.id;

  if ((file.mimeType ?? "") !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
    throw new Error("Resume must be a PDF.");
  }

  const path = `${userId}/${file.name}`;
  const { body } = await readUploadBody(file);

  const { error } = await supabase.storage.from("resumes").upload(path, body, {
    upsert: true,
    contentType: "application/pdf",
  });
  if (error) throw new Error(error.message);

  const { error: updateError } = await supabase.from("students").update({ resume_url: path }).eq("id", userId);
  if (updateError) throw new Error(updateError.message);

  return path;
}

export async function getResumeSignedUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from("resumes").createSignedUrl(path, 3600);
  if (error) return null;
  return data.signedUrl;
}
